<script lang="ts">
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Services
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import { format } from 'svelte-i18n';
	import { gen2Request } from '../../../services/gen_2_http_reqs';
	//Helpers
	import { get_gen_2_component_channel_structure } from '../../../helpers/interface_helpers';
	import { capFirstChar, getLimitedValue } from 'src/helpers/utilities';
	import {
		getVialFaultError,
		translateVialFaultError,
	} from '../../../helpers/notifications/device_notifications_helper';
	import { formatEmptyVialMessage } from '../../../helpers/cury_vial_helpers';
	import { unlockPin } from 'src/helpers/pin_lock_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { hasControlRights } from 'src/helpers/user_helpers';
	//Design system components
	import { Slider, Button, FlexibleCard, Text, Icon } from '@Shelly/shelly-design-system';
	import QuantityIndicator from '@Shelly/shelly-design-system/QuantityIndicator/QuantityIndicator.svelte';
	//Components
	import PinUnlock from '../../PinUnlock.svelte';
	//Types
	import type { SlotName } from 'src/types/Cury';
	import type { Device } from 'src/types/Device';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	export let device: Device, status: DeviceStatusG2;
	export let isSidePanelOpen = true;

	const component = get_gen_2_component_channel_structure(device);

	$: hasRights = hasControlRights(device);

	const loadingVial = {
		intensity: { left: false, right: false },
		boost: { left: false, right: false },
	};

	let pinLocked = {
		locked: false,
		slot: undefined as SlotName | undefined,
		value: undefined as number | undefined,
	};
	// Cache variables to prevent flickering
	let leftIntensityCache: number | undefined;
	let rightIntensityCache: number | undefined;
	let leftIntensityTarget: number | undefined;
	let rightIntensityTarget: number | undefined;
	let showModal = false; // pin unlock modal
	let onUnlockCallback: (() => void | Promise<void>) | null = null;

	// Keep local reference to slots in sync with incoming status updates
	$: slots = (status?.[component]?.slots || {
		left: null,
		right: null,
	}) as Record<SlotName, any>;

	// Get vial names directly from status
	$: leftName = slots?.left?.vial?.name || 'N/A';
	$: rightName = slots?.right?.vial?.name || 'N/A';

	// Check if away mode is active
	$: awayModeActive = status?.[component]?.away_mode || false;

	$: vialFault = getVialFaultError(status?.[component]?.slots);

	$: leftBoost = typeof slots?.left?.boost !== 'undefined' && slots?.left?.boost !== null;
	$: rightBoost = typeof slots?.right?.boost !== 'undefined' && slots?.right?.boost !== null; //this is for boost control visualization

	// Reactive boost labels
	$: leftBoostLabel = leftBoost
		? $format('label_off', { default: 'Off' })
		: $format('label_boost', { default: 'Boost' });
	$: rightBoostLabel = rightBoost
		? $format('label_off', { default: 'Off' })
		: $format('label_boost', { default: 'Boost' });

	// Simple reactive intensity values
	$: leftIntensity = slots?.left?.intensity ?? 0;
	$: rightIntensity = slots?.right?.intensity ?? 0;

	// Clear cache when target is reached (exactly like ControlPositionG2)
	$: if (leftIntensityTarget && slots?.left?.intensity === leftIntensityTarget) {
		leftIntensityTarget = undefined;
		leftIntensityCache = undefined;
	}

	$: if (rightIntensityTarget && slots?.right?.intensity === rightIntensityTarget) {
		rightIntensityTarget = undefined;
		rightIntensityCache = undefined;
	}

	$: if (typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock) {
		pinLocked.locked = true;
	}

	const setIntensity = async (e: CustomEvent, slot: string) => {
		if (!pinLocked.locked) {
			loadingVial.intensity[slot] = true;

			// Cache the current value before making changes (like ControlPositionG2)
			if (slot === 'left') {
				leftIntensityCache = leftIntensity;
				leftIntensityTarget = e.detail.value;
			} else {
				rightIntensityCache = rightIntensity;
				rightIntensityTarget = e.detail.value;
			}

			try {
				const res = await gen2Request(device, 'cury.Set', {
					id: device.channel,
					slot: slot,
					intensity: e.detail.value,
					on: slots[slot]?.on, //this is needed not to turn off the vial while changing the intensity
				});

				if (res?.isok) {
					onSettingsResponse(res, device, {}, false);
					slots[slot].intensity = e.detail.value; //this is needed to reduce flickering
				} else {
					throw new Error('Failed to set intensity');
				}
			} catch (err) {
				// Revert to cached value on failure
				if (slot === 'left') {
					leftIntensity = leftIntensityCache;
					leftIntensityTarget = undefined;
					leftIntensityCache = undefined;
				} else {
					rightIntensity = rightIntensityCache;
					rightIntensityTarget = undefined;
					rightIntensityCache = undefined;
				}
				console.log(err);
			} finally {
				loadingVial.intensity[slot] = false;
			}
		} else {
			pinLocked.locked = false;
			pinLocked.slot = slot as SlotName;
			pinLocked.value = e.detail.value;
			onUnlockCallback = () => {
				if (typeof pinLocked.slot !== 'undefined' && typeof pinLocked.value !== 'undefined') {
					setIntensity({ detail: { value: pinLocked.value } } as CustomEvent, pinLocked.slot);
				}
			};
			showModal = true;
		}
	};

	//ToDo steps must be removed

	const toggleBoost = async (slot: SlotName) => {
		if (!pinLocked.locked) {
			loadingVial.boost[slot] = true;
			let res: SettingRequest;
			const boosted = slots[slot]?.boost !== null; //the boost is object or null
			try {
				res = await gen2Request(
					device,
					boosted ? 'Cury.StopBoost' : 'Cury.Boost', //this is on off for the boost
					{
						id: device.channel,

						slot: slot,
					},
				);
				if (res?.isok) {
					// Update the local state to reflect the boost change
					if (boosted) {
						// If boost was active, set it to null (off)
						status[component].slots[slot].boost = null;
					} else {
						// If boost was inactive, set it to an object (on)
						status[component].slots[slot].boost = {};
					}

					loadingVial.boost[slot] = false;
				} else {
					throw new Error('Failed to toggle boost');
				}
			} catch (err) {
				console.log(err);
				loadingVial.boost[slot] = false;
			}
		} else {
			pinLocked.locked = false;
			pinLocked.slot = slot;
			onUnlockCallback = () => {
				if (typeof pinLocked.slot !== 'undefined') {
					toggleBoost(pinLocked.slot);
				}
			};
			showModal = true;
		}
	};

	const handlePin = (): void => {
		pinLocked.locked = unlockPin(device, pinLocked);
		if (!pinLocked.locked && onUnlockCallback) {
			onUnlockCallback();
			onUnlockCallback = null;
		}
	};

	const toggleIntensity = async (slot: SlotName) => {
		if (!pinLocked.locked) {
			loadingVial.intensity[slot] = true;
			let res;
			const vialIntensity = slots?.[slot]?.on;
			//http://192.168.33.1/rpc/Cury.Set?id=0&slot=%22left%22&on=false
			try {
				res = await gen2Request(device, 'cury.Set', {
					id: device.channel,

					slot: slot,
					on: !vialIntensity, //this is the on / off logic for the vial's intensity control
				});
				if (res?.isok) {
					onSettingsResponse(res, device, {}, false);
				}
			} catch (err) {
				console.log(err);
				loadingVial.intensity[slot] = false;
			}

			loadingVial.intensity[slot] = false;
			return res;
		} else {
			pinLocked.locked = false;
			pinLocked.slot = slot;
			onUnlockCallback = () => {
				if (typeof pinLocked.slot !== 'undefined') {
					toggleIntensity(pinLocked.slot);
				}
			};
			showModal = true;
		}
	};

	const handleClose = (): void => {
		pinLocked.locked = true;
		onUnlockCallback = null;
		// Revert cached values when modal is closed
		if (leftIntensityCache !== undefined) {
			leftIntensity = leftIntensityCache;
			leftIntensityTarget = undefined;
			leftIntensityCache = undefined;
		}
		if (rightIntensityCache !== undefined) {
			rightIntensity = rightIntensityCache;
			rightIntensityTarget = undefined;
			rightIntensityCache = undefined;
		}
	};

	// Calculate how many points should be filled based on the value
	const getFilledPoints: (value: number) => number = (value: number): number => {
		const max = 100;
		const step = max / 4;
		return Math.max(0, Math.min(4, Math.ceil(value / step))); //Expected behavior is as requested - value ≤ 0 → 0; (0,25] → 1; (25,50] → 2; (50,75] → 3; (75,100] → 4; >100 stays 4.
	};

	const a11yProps = {
		ariaLabel: $format('label_intensity', { default: 'Intensity' }),
		ariaLabelValue: capFirstChar(
			$format('current_intensity', {
				default: 'Current Intensity',
			}),
		),
	};

	const slotKeys: SlotName[] = ['left', 'right'];
</script>

{#each slotKeys as slot (slot)}
	{@const isSlotAvailable = slots?.[slot] !== null}
	<FlexibleCard
		class="mb-s"
		header={$format(`label_${slot}_vial`, { default: `${slot} Vial` })}
		padding="m"
		backgroundColor={$showNewLayout ? '--color-0' : isSidePanelOpen ? '--color-0' : '--color-1'}
	>
		{#if isSlotAvailable}
			<Text class="mb-l text-bold" styleType="body-small">
				{slot === 'left' ? leftName : rightName}
			</Text>
		{:else if !vialFault[slot]}
			<Text class="mb-l text-bold" styleType="body-small">
				{$format('label_no_vial_detected', {
					default: 'No Vial Detected',
				})}
			</Text>
		{/if}
		{#if isSlotAvailable && !vialFault[slot]}
			<Slider
				id="slider-7cxux9z"
				{a11yProps}
				value={slot === 'left'
					? leftIntensityTarget !== undefined
						? leftIntensityTarget
						: leftIntensity
					: rightIntensityTarget !== undefined
						? rightIntensityTarget
						: rightIntensity}
				title={$format('label_intensity', { default: 'Intensity' })}
				splitTitle
				disabled={awayModeActive ||
					slots?.[slot] === null ||
					(slot === 'left' ? leftBoost : rightBoost) ||
					!hasRights}
				type="incremented"
				titleTypography="tiny"
				step={1}
				class="mb-s"
				on:move={(e) => {
					if ($os === 'IOS') {
						hapticFeedback('selection');
					}
				}}
				on:value_change={(e) => setIntensity(e, slot)}
			/>
			<div class="d-flex justify-content-space-between mt-s mb-s">
				<Button
					icon={slots?.[slot]?.on ? 'fas fa-stop' : 'fas fa-play'}
					class="flex-1 mr-xs"
					variant="secondary"
					size="s"
					loading={loadingVial.intensity[slot]}
					disabled={awayModeActive ||
						slots?.[slot] === null ||
						slots?.[slot]?.boost ||
						!hasRights}
					on:click={() => toggleIntensity(slot)}
					>{status[component]?.slots?.[slot]?.on
						? capFirstChar($format('label_stop', { default: 'Stop' }))
						: capFirstChar($format('label_start', { default: 'Start' }))}</Button
				>
				<Button
					icon="fas fa-rocket"
					class="flex-1"
					variant="secondary"
					size="s"
					loading={loadingVial.boost[slot]}
					disabled={awayModeActive || slots?.[slot] === null || !hasRights}
					on:click={() => toggleBoost(slot)}
					>{capFirstChar(slot === 'left' ? leftBoostLabel : rightBoostLabel)}
				</Button>
			</div>
		{:else if vialFault[slot]}
			<Text class="text-color-warning mt-s" styleType="body">
				{vialFault[slot] === 'empty'
					? formatEmptyVialMessage(
							slots?.[slot]?.vial?.name,
							$format,
							'label_vial_empty_named',
							'{vialName} is empty. Please replace with a new vial.',
							'label_vial_empty',
							'No liquid in vial',
						)
					: translateVialFaultError(vialFault[slot])?.error}
			</Text>
		{:else}
			<Text class="text-color-muted mt-s" styleType="body-small">
				{$format('label_no_vial_info', {
					default: 'Please insert vial or check vial if you have one inserted.',
				})}
			</Text>
		{/if}
		<div slot="additional" class="quantity-indicator-fixed-width">
			{#if isSlotAvailable && !vialFault[slot]}
				<QuantityIndicator
					quantity={isSlotAvailable ? getFilledPoints(slots?.[slot]?.vial?.level) : undefined}
					value={isSlotAvailable
						? String(getLimitedValue(slots?.[slot]?.vial?.level, 100) || 0)
						: 'N/A'}
					icon={isSlotAvailable ? 'far fa-spray-can-sparkles' : 'fa fa-x'}
					iconClasses={isSlotAvailable
						? getFilledPoints(slots?.[slot]?.vial?.level) > 0
							? 'text-color-muted'
							: 'text-color-danger'
						: 'text-color-muted'}
				/>
			{:else if !vialFault[slot]}
				<Icon icon="fal fa-circle-xmark" size="l" class="text-color-warning" />
			{:else}
				<Icon icon="fal fa-exclamation-triangle" size="m" class="text-color-warning" />
			{/if}
		</div>
	</FlexibleCard>
{/each}
{#if showModal}
	<PinUnlock
		bind:showModal
		on:unlock={handlePin}
		on:close={handleClose}
		on:cancel={() => (showModal = false)}
	/>
{/if}

<style>
	.quantity-indicator-fixed-width {
		/* ToDo - this is a temporary fix to make the quantity indicator fixed width need to be reworked in ds*/
		width: 52px;
		flex: 0 0 auto;
		display: flex;
		height: 100%;
		justify-content: center;
		align-items: center;
	}
</style>
